import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Snackbar,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import { Coachee } from "../../models";
import { CoacheeService } from "../../services/coachingNavigator/coacheeService";
import config from "../../config";
import { Context } from "../context";
import ConfirmationPanel from "../confirmationPanel";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (coachee: Coachee) => void;
  initialCoachee?: Coachee | null;
  onSaved?: (coachee: Coachee) => void;
}

const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");

export default function CreateClientPanel({ open, onClose, onCreated, initialCoachee, onSaved }: Props) {
  const { t } = useTranslation();
  const appContext = React.useContext(Context);

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [active, setActive] = React.useState(true);
  const [bookedHours, setBookedHours] = React.useState("");
  const [bookedSessions, setBookedSessions] = React.useState("");
  const [completedHours, setCompletedHours] = React.useState("");
  const [completedSessions, setCompletedSessions] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [successSnackbarOpen, setSuccessSnackbarOpen] = React.useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = React.useState(false);

  const initialFormState = React.useMemo(() => ({
    name: initialCoachee?.name || "",
    email: initialCoachee?.email || "",
    active: initialCoachee?.active ?? true,
    bookedHours: initialCoachee?.bookedHours?.toString() || "",
    bookedSessions: initialCoachee?.bookedSessions?.toString() || "",
    completedHours: initialCoachee?.completedHours?.toString() || "",
    completedSessions: initialCoachee?.completedSessions?.toString() || "",
  }), [initialCoachee]);

  const resetForm = React.useCallback(() => {
    setName("");
    setEmail("");
    setActive(true);
    setBookedHours("");
    setBookedSessions("");
    setCompletedHours("");
    setCompletedSessions("");
    setEmailError("");
  }, []);

  const populateForm = React.useCallback(() => {
    if (!initialCoachee) {
      resetForm();
      return;
    }

    setName(initialCoachee.name || "");
    setEmail(initialCoachee.email || "");
    setActive(initialCoachee.active ?? true);
    setBookedHours(initialCoachee.bookedHours?.toString() || "");
    setBookedSessions(initialCoachee.bookedSessions?.toString() || "");
    setCompletedHours(initialCoachee.completedHours?.toString() || "");
    setCompletedSessions(initialCoachee.completedSessions?.toString() || "");
    setEmailError("");
  }, [initialCoachee, resetForm]);

  React.useEffect(() => {
    if (!open) return;
    if (initialCoachee) {
      populateForm();
    } else {
      resetForm();
    }
  }, [open, initialCoachee, populateForm, resetForm]);

  const isValidEmail = (value: string) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(value);
  };

  const hasChanges = React.useMemo(() => {
    return (
      name !== initialFormState.name ||
      email !== initialFormState.email ||
      active !== initialFormState.active ||
      bookedHours !== initialFormState.bookedHours ||
      bookedSessions !== initialFormState.bookedSessions ||
      completedHours !== initialFormState.completedHours ||
      completedSessions !== initialFormState.completedSessions
    );
  }, [name, email, active, bookedHours, bookedSessions, completedHours, completedSessions, initialFormState]);

  const handleWarningClose = React.useCallback(() => {
    if (hasChanges) {
      setWarningDialogOpen(true);
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const creatorEmail = (appContext.state.invoker?.email || "").trim();

    if (!trimmedName || !trimmedEmail) {
      setEmailError(!trimmedEmail ? t("quicktask.invalid_email") : "");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setEmailError(t("quicktask.invalid_email"));
      return;
    }

    const payload: Coachee = {
      id: initialCoachee?.id,
      name: trimmedName,
      email: trimmedEmail,
      // Persist creator for filtering on backend; keep existing creator if already present
      creator: (initialCoachee as any)?.creator || creatorEmail || undefined,
      active,
      bookedHours: bookedHours ? Number(bookedHours) : undefined,
      bookedSessions: bookedSessions ? Number(bookedSessions) : undefined,
      completedHours: completedHours ? Number(completedHours) : undefined,
      completedSessions: completedSessions ? Number(completedSessions) : undefined,
    };

    try {
      const saved = await coacheeService.save(payload);
      const isEdit = Boolean(initialCoachee?.id);
      if (isEdit) {
        onSaved?.(saved);
      } else if (saved?.active === true) {
        onCreated(saved);
      }
      setSuccessSnackbarOpen(true);
      resetForm();
      onClose();
    } catch (error) {
      console.error("Failed to save coachee", error);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleWarningClose}
        fullWidth
        maxWidth="sm" slotProps={{ paper: { sx: { backgroundColor: "white" } } }}
      >
        <DialogTitle>
          {initialCoachee ? t("coachingNavigator.clients.dialog.editTitle") : t("coachingNavigator.addCoachee")}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              required
              label={t("coachingNavigator.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />

            <TextField
              required
              label={t("coachingNavigator.email")}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              error={!!emailError}
              helperText={emailError || ""}
            />

            <FormControlLabel
              control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
              label={t("coachingNavigator.active")}
            />

            <TextField
              label={t("coachingNavigator.bookedHours")}
              type="number"
              value={bookedHours}
              onChange={(e) => setBookedHours(e.target.value)} slotProps={{ htmlInput: { min: 0 } }}
            />

            <TextField
              label={t("coachingNavigator.completedHours")}
              type="number"
              value={completedHours}
              onChange={(e) => setCompletedHours(e.target.value)} slotProps={{ htmlInput: { min: 0 } }}
            />

            <Divider sx={{ gridColumn: '1 / -1', width: '100%', my: 1 }} />

            <TextField
              label={t("coachingNavigator.bookedSessions")}
              type="number"
              value={bookedSessions}
              onChange={(e) => setBookedSessions(e.target.value)} slotProps={{ htmlInput: { min: 0 } }}
            />

            <TextField
              label={t("coachingNavigator.completedSessions")}
              type="number"
              value={completedSessions}
              onChange={(e) => setCompletedSessions(e.target.value)} slotProps={{ htmlInput: { min: 0 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleWarningClose}>{t("button.cancel")}</Button>
          <Button variant="contained" onClick={handleSave}>
            {initialCoachee ? t("coachingNavigator.clients.dialog.save") : t("button.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationPanel
        action="warning"
        showModal={warningDialogOpen}
        hideModal={() => setWarningDialogOpen(false)}
        confirmModal={() => {
          setWarningDialogOpen(false);
          resetForm();
          onClose();
        }}
      />

      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
          icon={<CheckIcon fontSize="inherit" />}
        >
          {t("coachingNavigator.coacheeCreated")}
        </Alert>
      </Snackbar>
    </>
  );
}
